import { TrendingUp, PoundSterling, Calendar, Percent, AlertTriangle, CheckCircle, Calculator, Building2 } from "lucide-react";

export function InvestorBadge() {
  return (
    <span className="px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium border border-blue-500/30 flex items-center gap-1.5" data-testid="investor-badge">
      <TrendingUp className="w-3 h-3" />
      Investor Lens
    </span>
  );
}

export function InvestorSummary({ analysis, investorContext }) {
  if (!analysis) return null;
  
  const strategy = investorContext?.investment_strategy || "buy_to_let";
  const strategyLabels = {
    buy_to_let: "Buy-to-Let Analysis",
    flip: "Flip Analysis",
    hmo: "HMO Analysis",
    serviced: "Serviced Accommodation",
  };

  return (
    <div className="mb-6 bg-blue-500/[0.08] border border-blue-500/20 rounded-2xl p-5" data-testid="investor-summary">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="w-4 h-4 text-blue-400" />
        <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
          {strategyLabels[strategy] || "Investment Analysis"}
        </h3>
      </div>
      
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {analysis.gross_yield && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Percent className="w-3 h-3 text-blue-300/60" />
              <span className="text-[10px] text-white/40 uppercase">Gross Yield</span>
            </div>
            <p className="text-lg font-bold text-white">{analysis.gross_yield.toFixed(1)}%</p>
          </div>
        )}
        
        {analysis.net_yield && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3 h-3 text-blue-300/60" />
              <span className="text-[10px] text-white/40 uppercase">Net Yield</span>
            </div>
            <p className="text-lg font-bold text-white">{analysis.net_yield.toFixed(1)}%</p>
          </div>
        )}
        
        {analysis.cash_on_cash && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <PoundSterling className="w-3 h-3 text-blue-300/60" />
              <span className="text-[10px] text-white/40 uppercase">Cash-on-Cash</span>
            </div>
            <p className="text-lg font-bold text-white">{analysis.cash_on_cash.toFixed(1)}%</p>
          </div>
        )}
        
        {analysis.payback_years && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Calendar className="w-3 h-3 text-blue-300/60" />
              <span className="text-[10px] text-white/40 uppercase">Payback</span>
            </div>
            <p className="text-lg font-bold text-white">{analysis.payback_years.toFixed(1)} yrs</p>
          </div>
        )}
      </div>
      
      {/* Summary Text */}
      {analysis.summary && (
        <p className="text-sm text-white/70 leading-relaxed">{analysis.summary}</p>
      )}
    </div>
  );
}

export function YieldComparison({ analysis }) {
  if (!analysis?.yield_comparison) return null;
  
  const { current, post_improvement, improvement_delta } = analysis.yield_comparison;
  const isPositive = improvement_delta > 0;
  
  return (
    <div className="mb-4 bg-white/5 border border-white/10 rounded-xl p-4" data-testid="yield-comparison">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-white/40" />
        <span className="text-xs font-semibold text-white/60">Yield Impact of Improvements</span>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-center">
          <p className="text-xs text-white/40 mb-1">Current</p>
          <p className="text-lg font-bold text-white/70">{current.toFixed(1)}%</p>
        </div>
        
        <div className="flex-1 mx-4 relative">
          <div className="h-0.5 bg-white/10 w-full absolute top-1/2 -translate-y-1/2" />
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded-full text-xs font-medium ${
            isPositive ? "bg-cipher-green/20 text-cipher-green" : "bg-amber-500/20 text-amber-400"
          }`}>
            {isPositive ? "+" : ""}{improvement_delta.toFixed(1)}%
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-white/40 mb-1">Post-Improvement</p>
          <p className="text-lg font-bold text-cipher-green">{post_improvement.toFixed(1)}%</p>
        </div>
      </div>
    </div>
  );
}

export function ROIProjection({ analysis, investorContext }) {
  if (!analysis?.roi_projection) return null;
  
  const { total_investment, projected_value, roi_percent, holding_period } = analysis.roi_projection;
  const isPositive = roi_percent > 0;
  
  return (
    <div className="mb-4 bg-white/5 border border-white/10 rounded-xl p-4" data-testid="roi-projection">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-white/40" />
          <span className="text-xs font-semibold text-white/60">{holding_period}-Year ROI Projection</span>
        </div>
        <span className={`text-sm font-bold ${isPositive ? "text-cipher-green" : "text-red-400"}`}>
          {isPositive ? "+" : ""}{roi_percent.toFixed(0)}%
        </span>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Total Investment</span>
          <span className="text-white/70">£{total_investment.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Projected Exit Value</span>
          <span className="text-white/70">£{projected_value.toLocaleString()}</span>
        </div>
        <div className="h-px bg-white/10 my-2" />
        <div className="flex justify-between text-xs">
          <span className="text-white/50 font-medium">Projected Profit</span>
          <span className={`font-medium ${isPositive ? "text-cipher-green" : "text-red-400"}`}>
            £{(projected_value - total_investment).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

export function InvestmentRisk({ analysis }) {
  if (!analysis?.risk_assessment) return null;
  
  const { level, factors } = analysis.risk_assessment;
  const isLow = level === "low";
  const isMedium = level === "medium";
  
  return (
    <div
      className={`mb-4 flex items-start gap-3 px-4 py-3 rounded-xl border ${
        isLow
          ? "bg-cipher-green/[0.06] border-cipher-green/20"
          : isMedium
          ? "bg-amber-500/[0.06] border-amber-500/15"
          : "bg-red-500/[0.06] border-red-500/15"
      }`}
      data-testid="investment-risk"
    >
      {isLow ? (
        <CheckCircle className="w-4 h-4 text-cipher-green mt-0.5 flex-shrink-0" />
      ) : (
        <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isMedium ? "text-amber-400" : "text-red-400"}`} />
      )}
      <div>
        <p className="text-xs font-semibold text-white/70 mb-1 capitalize">
          {level} Risk Investment
        </p>
        {factors && factors.length > 0 && (
          <ul className="text-xs text-white/50 space-y-0.5">
            {factors.map((factor, i) => (
              <li key={i}>• {factor}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
